import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Search, Scissors, Calendar, Download, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/lib/confirm";

function fmtDate(iso) {
  if (!iso) return "—";
  try { const [y, m, d] = iso.substring(0, 10).split("-"); return `${d}/${m}/${y}`; } catch { return iso; }
}

const STATUS_COLORS = {
  importado: "bg-slate-100 text-slate-700",
  filtrado: "bg-slate-100 text-slate-700",
  em_revisao: "bg-amber-50 text-amber-800 border-amber-200",
  documentos_a_gerar: "bg-amber-50 text-amber-800 border-amber-200",
  documentos_gerados: "bg-blue-50 text-blue-800 border-blue-200",
  aguardando_assinados: "bg-blue-50 text-blue-800 border-blue-200",
  assinados_enviados: "bg-sky-50 text-sky-800 border-sky-200",
  divergencia_detectada: "bg-red-50 text-red-700 border-red-200",
  divergencia_revisada: "bg-amber-50 text-amber-800 border-amber-200",
  pronto_faturamento: "bg-emerald-50 text-emerald-800 border-emerald-200",
  faturado: "bg-emerald-100 text-emerald-900 border-emerald-300",
  cancelado: "bg-slate-100 text-slate-500 line-through",
};

export default function CasesPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "admin_master";
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(params.get("status") || "");
  const [surgeonId, setSurgeonId] = useState(params.get("surgeon_id") || "");
  const [procedureId, setProcedureId] = useState(params.get("procedure_id") || "");
  const [hospitalId, setHospitalId] = useState(params.get("hospital_id") || "");
  const [dateFrom, setDateFrom] = useState(params.get("date_from") || "");
  const [dateTo, setDateTo] = useState(params.get("date_to") || "");
  const [todayOnly, setTodayOnly] = useState(params.get("today") === "1");
  const [discrepancy, setDiscrepancy] = useState(params.get("discrepancy") === "1");
  const [proBono, setProBono] = useState(params.get("pro_bono") || ""); // "", "true", "false"
  const [surgeons, setSurgeons] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [patients, setPatients] = useState({});

  const todayISO = useMemo(() => new Date().toISOString().substring(0, 10), []);

  // Filtros compartilhados entre listagem e exportações (Excel/CSV)
  function queryParams() {
    const p = {};
    if (search) p.search = search;
    if (status) p.status = status;
    if (surgeonId) p.surgeon_id = surgeonId;
    if (procedureId) p.procedure_id = procedureId;
    if (hospitalId) p.hospital_id = hospitalId;
    if (todayOnly) { p.date_from = todayISO; p.date_to = todayISO; }
    else {
      if (dateFrom) p.date_from = dateFrom;
      if (dateTo) p.date_to = dateTo;
    }
    return p;
  }

  async function load() {
    try {
      const r = await api.get("/cases", { params: queryParams() });
      let data = r.data || [];
      if (discrepancy) {
        data = data.filter((c) => (c.discrepancies || []).length > 0 || c.status === "divergencia_detectada");
      }
      if (proBono === "true") data = data.filter((c) => !!c.pro_bono);
      else if (proBono === "false") data = data.filter((c) => !c.pro_bono);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function deleteCase(c, e) {
    e.stopPropagation();
    if (!isAdmin) return;
    if (!(await confirm({ description: `Excluir o caso ${c.case_code}?\n\n` +
      `Linha do mapa filtrado, divergências e documentos vinculados também serão removidos.\n` +
      `Produção/Faturamento/Financeiro recalculam automaticamente.\n\n` +
      `Esta ação não pode ser desfeita.` }))) return;
    try {
      await api.delete(`/cases/${c.id}`);
      toast.success(`Caso ${c.case_code} excluído.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao excluir");
    }
  }

  // Keep URL in sync when filters change
  useEffect(() => {
    const next = new URLSearchParams();
    if (status) next.set("status", status);
    if (surgeonId) next.set("surgeon_id", surgeonId);
    if (procedureId) next.set("procedure_id", procedureId);
    if (hospitalId) next.set("hospital_id", hospitalId);
    if (dateFrom) next.set("date_from", dateFrom);
    if (dateTo) next.set("date_to", dateTo);
    if (todayOnly) next.set("today", "1");
    if (discrepancy) next.set("discrepancy", "1");
    if (proBono) next.set("pro_bono", proBono);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, surgeonId, procedureId, hospitalId, dateFrom, dateTo, todayOnly, discrepancy, proBono]);

  useEffect(() => {
    api.get("/surgeons").then((r) => setSurgeons(r.data || []));
    api.get("/hospitals").then((r) => setHospitals(r.data || [])).catch(() => {});
    api.get("/cases/statuses").then((r) => setStatuses(r.data || []));
    api.get("/patients").then((r) => {
      const m = {};
      (r.data || []).forEach((p) => { m[p.id] = p; });
      setPatients(m);
    });
  }, []);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [search, status, surgeonId, procedureId, hospitalId, dateFrom, dateTo, todayOnly, discrepancy, proBono]);

  const anyFilter = status || surgeonId || procedureId || hospitalId || dateFrom || dateTo || todayOnly || discrepancy || proBono || search;
  function clearAll() {
    setStatus(""); setSurgeonId(""); setProcedureId(""); setHospitalId(""); setDateFrom(""); setDateTo("");
    setTodayOnly(false); setDiscrepancy(false); setProBono(""); setSearch("");
  }
  const fmtBR = (d) => (d ? d.split("-").reverse().join("/") : "");
  const periodLabel = dateFrom && dateTo ? (dateFrom === dateTo ? fmtBR(dateFrom) : `${fmtBR(dateFrom)} – ${fmtBR(dateTo)}`)
    : dateFrom ? `a partir de ${fmtBR(dateFrom)}` : dateTo ? `até ${fmtBR(dateTo)}` : "";

  return (
    <div className="space-y-5" data-testid="cases-page">
      {procedureId === "__none__" && (
        <div
          className="border border-amber-200 bg-amber-50 text-amber-900 rounded-md px-4 py-3 text-sm flex items-start gap-3"
          data-testid="banner-no-procedure-filter"
        >
          <span className="text-lg leading-none">⚠</span>
          <div className="flex-1">
            <div className="font-medium">Filtro ativo: casos <strong>sem procedimento identificado</strong></div>
            <div className="text-xs mt-1">
              Estes casos foram criados a partir de linhas do mapa que o sistema não conseguiu vincular a nenhum procedimento do catálogo.
              Abra cada caso para ver o texto bruto e cadastrar o procedimento correspondente em <em>Procedimentos</em> — assim o vínculo é refeito automaticamente.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setProcedureId("")}
            className="text-xs underline text-amber-800 hover:text-amber-900 ml-auto whitespace-nowrap"
            data-testid="btn-clear-no-procedure"
          >
            Limpar filtro
          </button>
        </div>
      )}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Casos Cirúrgicos</h1>
          <p className="text-sm text-slate-500 mt-1">Cirurgias da equipe com status, documentos e faturamento.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={async () => {
            try {
              await downloadFile("/reports/cases.xlsx", "casos.xlsx", queryParams());
              toast.success("Excel gerado.");
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }} data-testid="btn-export-cases-xlsx">
            <Download className="h-4 w-4 mr-1.5" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={async () => {
            try {
              await downloadFile("/reports/cases.csv", "casos.csv", queryParams());
              toast.success("CSV gerado.");
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }} data-testid="btn-export-cases-csv">
            <Download className="h-4 w-4 mr-1.5" /> CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Código ou procedimento..."
            value={search} onChange={(e) => setSearch(e.target.value)} data-testid="cases-search" />
        </div>
        <select className="border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="cases-filter-status">
          <option value="">Todos os status</option>
          {statuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select className="border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white" value={surgeonId} onChange={(e) => setSurgeonId(e.target.value)} data-testid="cases-filter-surgeon">
          <option value="">Todos os cirurgiões</option>
          {surgeons.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
        </select>
        <select className="border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white" value={proBono} onChange={(e) => setProBono(e.target.value)} data-testid="cases-filter-pro-bono" title="Filtrar por tipo: remuneradas (faturáveis) ou pro bono (sem efeito financeiro)">
          <option value="">Todas (remuneradas + pro bono)</option>
          <option value="false">Só remuneradas</option>
          <option value="true">Só pro bono</option>
        </select>
        <div className="flex items-center gap-2">
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium w-8">De</label>
          <Input type="date" value={dateFrom} disabled={todayOnly}
            onChange={(e) => setDateFrom(e.target.value)} data-testid="cases-filter-date-from" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium w-8">Até</label>
          <Input type="date" value={dateTo} disabled={todayOnly}
            onChange={(e) => setDateTo(e.target.value)} data-testid="cases-filter-date-to" />
        </div>
        <select className="border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white md:col-span-2" value={hospitalId} onChange={(e) => setHospitalId(e.target.value)} data-testid="cases-filter-hospital">
          <option value="">Todos os hospitais</option>
          {hospitals.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
        </select>
      </div>

      {(todayOnly || discrepancy || anyFilter) && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {periodLabel && !todayOnly && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-800 border border-blue-200" data-testid="chip-period">
              Período: {periodLabel}
              <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="ml-1" data-testid="chip-period-clear"><X className="h-3 w-3" /></button>
            </span>
          )}
          {hospitalId && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-50 text-sky-800 border border-sky-200" data-testid="chip-hospital">
              {hospitals.find((h) => h.id === hospitalId)?.name || "Hospital"}
              <button onClick={() => setHospitalId("")} className="ml-1"><X className="h-3 w-3" /></button>
            </span>
          )}
          {todayOnly && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
              Hoje ({todayISO.split("-").reverse().join("/")})
              <button onClick={() => setTodayOnly(false)} className="ml-1"><X className="h-3 w-3" /></button>
            </span>
          )}
          {discrepancy && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">
              Com divergência
              <button onClick={() => setDiscrepancy(false)} className="ml-1"><X className="h-3 w-3" /></button>
            </span>
          )}
          {proBono && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
              {proBono === "true" ? "Só pro bono" : "Só remuneradas"}
              <button onClick={() => setProBono("")} className="ml-1"><X className="h-3 w-3" /></button>
            </span>
          )}
          {anyFilter && (
            <Button size="sm" variant="ghost" onClick={clearAll} className="text-slate-600 h-7" data-testid="btn-clear-cases-filters">
              <X className="h-3 w-3 mr-1" /> Limpar filtros
            </Button>
          )}
          <span className="text-slate-500 ml-auto">{items.length} caso(s)</span>
        </div>
      )}

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-4 py-3">Código</th>
                <th className="text-left px-4 py-3">Data</th>
                <th className="text-left px-4 py-3">Paciente</th>
                <th className="text-left px-4 py-3">Procedimento</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={6} className="text-center p-8 text-slate-400">Nenhum caso encontrado.</td></tr>}
              {items.map((c, idx) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/50 cursor-pointer" onClick={() => nav(`/casos/${c.id}`)} data-testid={`cases-row-${idx}`}>
                  <td className="px-4 py-3 font-mono text-xs">{c.case_code}</td>
                  <td className="px-4 py-3"><Calendar className="h-3 w-3 inline mr-1 text-slate-400" /> {fmtDate(c.surgery_date)} {c.surgery_time}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">
                    <div className="flex items-center gap-2">
                      <span>{patients[c.patient_id]?.full_name || "—"}</span>
                      {c.pro_bono && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold uppercase tracking-wider" title="Cirurgia pro bono — não gera faturamento" data-testid={`row-pro-bono-badge-${idx}`}>
                          Pro Bono
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Scissors className="h-3.5 w-3.5 text-slate-400" />
                      {c.procedure_name || "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${STATUS_COLORS[c.status] || "bg-slate-100 text-slate-700"}`}>
                      {c.status?.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); nav(`/casos/${c.id}`); }}>Abrir</Button>
                      {isAdmin && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:bg-red-50 hover:text-red-700"
                          onClick={(e) => deleteCase(c, e)}
                          data-testid={`btn-delete-case-${idx}`}
                          title="Excluir caso (admin)"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
